import { ArrowRight, BadgeDollarSign, Check, CheckSquare, Clock3, Tag, X, Zap } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const firstDelayMs = 20 * 1000;
const secondDelayMs = 150 * 1000;
const maxAppearances = 2;

function AgentPreviewCard() {
  return (
    <div className="builder-invite-preview" aria-hidden="true">
      <div className="builder-invite-preview-card preview-card-back" />
      <div className="builder-invite-preview-card preview-card-mid" />
      <div className="builder-invite-preview-card">
        <div className="builder-invite-preview-top">
          <strong>AI Lead Qualifier</strong>
          <span>$49</span>
        </div>
        <p>Qualify leads and extract key details from inquiries.</p>
        <div className="builder-invite-preview-step">
          <CheckSquare size={13} />
          <div>
            <strong>Input</strong>
            <span>Inquiry data</span>
          </div>
        </div>
        <div className="builder-invite-preview-step">
          <Check size={13} />
          <div>
            <strong>Output</strong>
            <span>Qualified leads</span>
          </div>
        </div>
        <div className="builder-invite-preview-footer">
          <span>4.9 (32)</span>
          <strong>A</strong>
        </div>
      </div>
    </div>
  );
}

export function BuilderInviteModal({ currentUser }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [appearances, setAppearances] = useState(0);
  const timerRef = useRef(null);
  const isSuppressed = useMemo(
    () => Boolean(currentUser) || location.pathname.startsWith('/studio') || location.pathname === '/list-your-agent',
    [currentUser, location.pathname]
  );

  useEffect(() => {
    window.clearTimeout(timerRef.current);

    if (isSuppressed || isOpen || appearances >= maxAppearances) {
      if (isSuppressed) {
        setIsOpen(false);
      }
      return undefined;
    }

    const delay = appearances === 0 ? firstDelayMs : secondDelayMs;
    timerRef.current = window.setTimeout(() => {
      setIsOpen(true);
      setAppearances((current) => current + 1);
    }, delay);

    return () => window.clearTimeout(timerRef.current);
  }, [appearances, isOpen, isSuppressed]);

  useEffect(() => {
    if (!isOpen || isSuppressed) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isSuppressed]);

  if (!isOpen || isSuppressed) {
    return null;
  }

  return (
    <div className="builder-invite-backdrop" role="presentation">
      <section
        className="builder-invite-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="builder-invite-title"
      >
        <button
          type="button"
          className="builder-invite-close"
          aria-label="Close builder invite"
          onClick={() => setIsOpen(false)}
        >
          <X size={24} />
        </button>

        <div className="builder-invite-content">
          <div className="builder-invite-main">
            <span className="builder-invite-kicker">
              <CheckSquare size={14} />
              Builder invite
            </span>
            <h2 id="builder-invite-title">Built an AI agent?</h2>
            <p className="builder-invite-lede">
              List it on AgenticHire and get discovered by buyers looking for real AI services.
            </p>
            <p className="builder-invite-copy">
              Turn your agent into a fixed-price service with clear inputs and outputs.
            </p>

            <div className="builder-invite-points">
              <div className="builder-invite-point">
                <span>
                  <Tag size={18} />
                </span>
                <strong>Get discovered by early buyers</strong>
              </div>
              <div className="builder-invite-point">
                <span>
                  <BadgeDollarSign size={18} />
                </span>
                <strong>Only 5% platform fee</strong>
              </div>
              <div className="builder-invite-point">
                <span>
                  <Zap size={18} />
                </span>
                <strong>List your agent in minutes</strong>
              </div>
            </div>
          </div>

          <div className="builder-invite-art">
            <AgentPreviewCard />
          </div>
        </div>

        <div className="builder-invite-strip" aria-label="Builder benefits">
          <div>
            <Tag size={22} />
            <strong>Fixed-price offers</strong>
          </div>
          <div>
            <CheckSquare size={22} />
            <strong>Defined outputs</strong>
          </div>
          <div>
            <Clock3 size={22} />
            <strong>Fast setup</strong>
          </div>
        </div>

        <div className="builder-invite-actions">
          <Link to="/studio" className="primary-button builder-invite-primary" onClick={() => setIsOpen(false)}>
            Start having your agent earn for you
          </Link>
          <Link
            to="/how-agentichire-works"
            className="builder-invite-secondary"
            onClick={() => setIsOpen(false)}
          >
            How it works
            <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
